import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DollarSign, TrendingUp, TrendingDown, FileText, Building2, CreditCard } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, ExcludedNote, SectionNote, money, count,
} from '../../components/admin/AdminKit';
import AdminPaymentsRefunds from '../../components/admin/AdminPaymentsRefunds'; // WO-38

// WO-36 — revenue from records only: subscriptions live in Stripe, the
// cancellations Stripe reported, and paid report purchases. The old page
// multiplied plan labels by list prices and showed two different MRRs.

export default function AdminRevenue() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/revenue/overview');
  if (loading) return <LoadingBlock testid="admin-revenue-loading" />;
  const byPlan = data?.by_plan || {};
  const rep = data?.reports_revenue || {};
  const canc = data?.cancellations_30d || {};

  return (
    <div className="space-y-6" data-testid="admin-revenue-page">
      <div>
        <h1 className="text-2xl font-bold">Revenue</h1>
        <p className="text-muted-foreground">Subscriptions live in Stripe, cancellations and report sales</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <ExcludedNote excluded={data.excluded} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="MRR" icon={DollarSign} value={money(data.mrr?.total)} note="Live subscriptions, list price" testid="rev-mrr" />
            <Metric label="ARR" icon={TrendingUp} value={money(data.arr)} note="MRR × 12" />
            <Metric label="Cancelled (30 days)" icon={TrendingDown} value={count(canc.total)}
              note={`${count(canc.personal || 0)} personal · ${count(canc.enterprise || 0)} Enterprise`} />
            <Metric label="Reports sold" icon={FileText} value={money(rep.revenue)}
              note={`${count(rep.total_sold)} sold · ${money(rep.revenue_30d)} in the last 30 days`} testid="rev-reports" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" />MRR by plan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(byPlan).map(([plan, p]) => (
                  <div key={plan} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">{plan}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {p.subscriptions} subscription{p.subscriptions === 1 ? '' : 's'}{plan === 'enterprise' ? ` · ${p.seats} seats` : ''}
                      </span>
                    </div>
                    <span className="font-bold">{money(p.mrr)}/mo</span>
                  </div>
                ))}
                {Object.keys(byPlan).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No live paid subscription yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" />Paying customers</CardTitle>
                <CardDescription>Largest first</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {(data.customers || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.account || '—'}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {c.plan} · {c.cycle}{c.kind === 'enterprise' ? ` · ${c.seats} seats` : ''}{c.status === 'past_due' ? ' · payment overdue' : ''}
                      </p>
                    </div>
                    <span className="font-bold text-green-600">{money(c.monthly)}/mo</span>
                  </div>
                ))}
                {(data.customers || []).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No paying customer yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Metric label="New, upgrade, downgrade and cancelled MRR" value={null}
              why="Needs a history of plan changes (Phase 2)" />
            <Metric label="Month-by-month revenue" value={null}
              why="Needs a history of plan changes (Phase 2)" />
          </div>
          <SectionNote>
            Stripe's own dashboard remains the record of money actually collected and referral discounts.
          </SectionNote>
        </>
      )}
      {/* WO-38 — shown even when the revenue figures fail to load */}
      <AdminPaymentsRefunds onChanged={reload} />
    </div>
  );
}
